import { z } from 'zod';

import { countryCodeSchema } from '@apple/utils/globalization/models';
import type { PaginatedListDto } from '@apple/utils/pagination/models';
import { paginatedListDtoSchema } from '@apple/utils/pagination/models';

import { customerIdSchema } from '@apple/features/customer/management.models';
import type { BaseOrderSearchListing } from '@apple/features/order/models/search';
import { baseOrderSearchListingSchema } from '@apple/features/order/models/search';
import {
	type OrderCommentStatus,
	orderCommentStatusSchema,
} from '@apple/features/order/models/status';
import { programIdSchema } from '@apple/features/program/models';

/** @see AppleRetail.Web.Features.Orders.AppleRetailOrderSearchController.OrderFilter */
export type OrderSearchFilters = z.infer<typeof orderSearchFiltersSchema>;
export type OrderSearchTableData = OrderListing & OrderSearchFilters;

/** @see AppleRetail.Web.Features.Orders.AppleRetailOrderSearchController.OrderListing */
export interface OrderListing extends BaseOrderSearchListing {
	storeName: string;
	createdBy: string;
	orderReason: string;
	thirdPartyCustomerId: string;
	customerCode: string;
	orderCommentStatus: OrderCommentStatus | null;
}

/** @see AppleRetail.Web.Features.Orders.AppleRetailOrderSearchController.OrderFilter */
export const orderSearchFiltersSchema = z
	.object({
		orderNumber: z.string(),
		orderStatus: z.string(),
		customerIds: customerIdSchema.array(),
		warehouseCode: z.string(),
		countryCode: countryCodeSchema,
		customerGroupId: programIdSchema,
		orderDateRangeInDays: z.number().or(z.literal('custom')),
		startDate: z.coerce.date().or(z.string()),
		endDate: z.coerce.date(),
		partNumber: z.string(),
		searchTerm: z.string(),
		orderReason: z.string(),
		showCancelledDistroOrders: z.boolean(),
	})
	.partial();

export interface OrderSearchResponse {
	success: boolean;
	reason: number;
	orders: PaginatedListDto<OrderListing>;
}

export const orderListingSchema = baseOrderSearchListingSchema.extend({
	storeName: z.string(),
	createdBy: z.string(),
	orderReason: z.string(), // can't use orderReasonSchema because this is a display value
	thirdPartyCustomerId: z.string(),
	customerCode: z.string(),
	orderCommentStatus: orderCommentStatusSchema.nullable(),
}) satisfies z.ZodType<OrderListing>;

export const orderSearchResponseSchema = z.object({
	success: z.boolean(),
	reason: z.number(),
	orders: paginatedListDtoSchema(orderListingSchema),
}) satisfies z.ZodType<OrderSearchResponse>;

/** @see AppleRetail.Web.Features.Orders.RecentOrdersController.RecentOrderListing */
export const recentOrderListingSchema = z.object({
	orderDate: z.coerce.date(),
	purchaseOrderNumber: z.string(),
	orderReason: z.string(),
	status: z.string(),
	productCount: z.string(),
	orderCommentStatus: orderCommentStatusSchema.nullable(),
});

export type RecentOrderListing = z.infer<typeof recentOrderListingSchema>;
